import { type DefaultTreeAdapterMap, parse as parseHTMLString } from "parse5";

type ChildNode = DefaultTreeAdapterMap["childNode"];
type Document = DefaultTreeAdapterMap["document"];

type CleanDocument = {
  type: string;
  value?: string;
  children?: CleanDocument[];
  array?: boolean;
};

type FlatDocument = {
  [key: string]: string | FlatDocument | FlatDocument[];
};

/**
 * Parses an HTML string, filters and processes the resulting DOM structure, and returns
 * a flattened and more readable representation of the DOM.
 *
 * @param {string} html - The HTML string to parse and process.
 * @returns {FlatDocument} - The flattened and more readable representation of the DOM structure.
 */
export const parseHTML = (html: string): FlatDocument => {
  const parsed = parseHTMLString(html);
  const transformed = filterAndProcessDom(parsed);
  const fattened = flattenDocument(transformed);
  return fattened;
};

/**
 * Filter and process the DOM node, removes unwanted elements and
 * returning a CleanDocument object with the relevant node data.
 *
 * @param {ChildNode | Document} node - The DOM node to process and filter.
 * @returns {CleanDocument} - A CleanDocument object containing the processed node data.
 */
const filterAndProcessDom = (node: ChildNode | Document): CleanDocument => {
  if (
    // Exclude
    node.nodeName === "head" ||
    node.nodeName === "style" ||
    node.nodeName === "script" ||
    node.nodeName === "form" ||
    node.nodeName === "#comment" ||
    node.nodeName === "template" ||
    node.nodeName === "#documentType"
  ) {
    return { type: "#undefined" };
  } else if (node.nodeName === "br" || node.nodeName === "hr") {
    // br, hr, ... are just text
    return { type: "#undefined", value: "\n" };
  } else if (node.nodeName === "a") {
    const href = node.attrs.find((a) => a.name === "href")?.value ?? "";
    return {
      type: "a",
      array: false,
      children: [
        {
          type: "href",
          value: href,
        },
        ...node.childNodes.map((node) => filterAndProcessDom(node)).filter((node) => node.type !== "#undefined"),
      ],
    };
  } else if (node.nodeName === "img") {
    return {
      type: "img",
      value: node.attrs.find((a) => a.name === "src")?.value ?? "",
    };
  } else if (node.nodeName === "#text" || node.nodeName === "title") {
    // Just text
    if ("value" in node) {
      // Whitespace has already been collapsed to single spaces upstream,
      // so a lone " " is not content.
      const text = node.value.trim();
      if (text.length > 0) {
        return { type: "#text", value: text };
      }
    }
  } else if ("childNodes" in node) {
    // HTML Tag
    const childNodes = node.childNodes
      .map((node) => filterAndProcessDom(node))
      .filter((node) => node.type !== "#undefined");
    return {
      type: node.nodeName,
      array: isArray(childNodes),
      children: childNodes,
    };
  }
  return { type: "#undefined" };
};

/**
 * Determines if all children in the given array of CleanDocument elements have a similar structure.
 *
 * @param {Array<CleanDocument>} elements - The array of CleanDocument elements to check.
 * @returns {boolean} - Returns true if all children have a similar structure, and false otherwise.
 */
const isArray = (elements: CleanDocument[]): boolean => {
  if (elements.length < 2) return false;

  // Compare every sibling against the first, not pairwise-adjacent:
  // similarity is not transitive, so the chain let drift through.
  return elements.every((el) => isSimilarStructure(elements[0], el));
};

/**
 * Structural similarity of two parsed nodes.
 *
 * Compares two objects for structural similarity at the top-most level.
 * Returns true if the similarity ratio (e) is greater than or equal to the given epsilon value (0.8),
 * and false otherwise.
 *
 * @param {Record<string, unknown>} a - The first object to compare.
 * @param {Record<string, unknown>} b - The second object to compare.
 * @returns {boolean} - Returns true if the objects have a similar structure, and false otherwise.
 */
const isSimilarStructure = (a: CleanDocument, b: CleanDocument): boolean => {
  const EPSILON = 0.8;

  // Same tag is the primary signal.
  if (a.type !== b.type) return false;

  const aLeaf = a.children === undefined || a.children.length === 0;
  const bLeaf = b.children === undefined || b.children.length === 0;
  // Both leaves of the same tag: same shape regardless of text content.
  if (aLeaf && bLeaf) return true;
  if (aLeaf !== bLeaf) return false;

  const aChildren = a.children as CleanDocument[];
  const bChildren = b.children as CleanDocument[];

  // Multiset of child tags, compared by overlap.
  const count = (nodes: CleanDocument[]) =>
    nodes.reduce<Map<string, number>>((m, n) => m.set(n.type, (m.get(n.type) ?? 0) + 1), new Map());
  const ca = count(aChildren);
  const cb = count(bChildren);

  let shared = 0;
  for (const [tag, n] of ca) shared += Math.min(n, cb.get(tag) ?? 0);

  const ratio = shared / Math.max(aChildren.length, bChildren.length);
  // ponytail: one level of child tags, no recursion into grandchildren.
  // Upgrade path: recurse with a depth budget if nested tables mis-group.
  return ratio >= EPSILON;
};

/**
 * Transforms a CleanDocument structure into a more readable and flattened structure.
 *
 * @param {CleanDocument} input - The input CleanDocument structure to be transformed.
 * @returns {FlatDocument} - The transformed structure as a FlatDocument object.
 */
function flattenDocument(input: CleanDocument): FlatDocument {
  const traverse = (node: CleanDocument, keyPrefix: string, resultObject: FlatDocument) => {
    if (node.value !== undefined) {
      resultObject[keyPrefix + node.type] = node.value;
    }
    if (node.children) {
      if (node.array) {
        const res = node.children.map((child) => traverse(child, "", {}));
        resultObject[keyPrefix + node.type] = res;
      } else if (node.children.length === 0) {
        // Do nothing.
      } else if (node.children.length === 1) {
        resultObject[keyPrefix + node.type] = traverse(node.children[0], "", {});
      } else {
        resultObject[keyPrefix + node.type] = {};
        const countingMap: Record<string, number> = {};
        node.children.forEach((child) => {
          countingMap[child.type] = (countingMap[child.type] || 0) + 1;
          traverse(child, countingMap[child.type] + "_", resultObject[keyPrefix + node.type] as FlatDocument);
        });
      }
    }
    return resultObject;
  };

  return traverse(input, "", {});
}
